import * as React from 'react';
import { View, Text, StyleSheet, ImageBackground, TextInput, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { useForm, Controller } from "react-hook-form";
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import Constants from "expo-constants";
import { UserService } from '../store/services/user-service';
import AsyncStorage from '@react-native-async-storage/async-storage';
import GlobalStyle from '../constants/GlobalStyle';
import config from '../config';
import {setUser, User} from '../store/slices/user-slice';
import {UserRole} from "../store/data/users/user-roles";

const VERSION = Constants.expoConfig.version;

export default function LoginScreen({navigation}: {navigation: any}) {
  const dispatch = useDispatch();
  const userService = new UserService();

  const [loading, setLoading] = useState(false);
  const [loginResult, setLoginResult] = useState(null);


  const [mode, setMode] = useState<'login' | 'request' | 'reset' | 'forceReset'>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });


  const onSubmit = async (data) => {
    const email = data.email;
    const password = data.password;

    setLoading(true);
    const loginResult = await userService.login(email, password);
    setLoading(false);
    if (loginResult.error) {
        return;
    }

    const resultData = loginResult.data as User & { requirePasswordReset?: boolean };

    console.log(resultData);

    if (resultData.requirePasswordReset) {
        setMode('forceReset');
        setResetEmail(email);
        setCurrentPassword(password); 
        return;
    }

    if (!resultData.token) {
        setResetError("Login failed: missing authentication token.");
        return;
    }

    setLoginResult(loginResult);
    dispatch(setUser(resultData));
    navigateUser(resultData.role);
};

  const navigateUser = (role: UserRole) => {
    console.log(role);
    if (role === 'Technician' || role === 'ExternalTechnician') {
      navigation.replace('technician');
      return;
    }

    navigation.replace('web');
  }

  const handleRequestReset = async () => {
    setResetError(null);
    setLoading(true);

    const result = await userService.requestPasswordReset(resetEmail);

    setLoading(false);
    if (result.error) {
      setResetError("Email not found.");
      return;
    }

    setMode('reset');
  };

  const validatePassword = (password: string) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

    if (!passwordRegex.test(password)) {
      return "Password must be at least 8 characters and include 1 uppercase letter, 1 lowercase letter, and 1 number.";
    }

    return null;
  };

  const handleResetPassword = async () => {
    setResetError(null);

    if (!resetCode || resetCode.length !== 4) {
      setResetError("Please enter the 4 digit reset code.");
      return;
    }

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setResetError(passwordValidationError);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetError("Passwords do not match.");
      return;
    }

    setLoading(true);

    const result = await userService.resetPassword(
      resetEmail,
      resetCode,
      newPassword
    );

    setLoading(false);

    if (result.error) {
      setResetError("Invalid code or reset request expired.");
      return;
    }


    setMode('login');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetError(null);
  };

  const handleForceResetPassword = async () => {
    setResetError(null);

    if (!currentPassword) {
        setResetError("Current password is required.");
        return;
    }

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
        setResetError(passwordValidationError);
        return;
    }

    if (newPassword !== confirmNewPassword) {
        setResetError("Passwords do not match.");
        return;
    }
    if (newPassword === currentPassword) {
        setResetError("New password must be different from current password.");
        return;
    }

    setLoading(true);
    const result = await userService.resetPasswordWithCurrent(
        resetEmail,
        currentPassword,
        newPassword
    );
    setLoading(false);

    if (result.error) {
        setResetError("Failed to reset password. Please check your current password.");
        return;
    }

    setMode('login');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetError(null);
};


  return (
    <ImageBackground
      source={require("../assets/images/login.png")}
      resizeMode='contain'
      style={styles.container}
    >
      <View style={{ position: 'absolute', top: 150, left: 0, right: 0 }}>
        <Text style={{ fontSize: 48, color: '#339af0', textAlign: 'center', fontWeight: 'bold' }}>
          Facilities Assets Platform
        </Text>
      </View>

      <View style={styles.login}>
        {loading &&
          <View style={styles.loader}>
            <ActivityIndicator size="large" color={config.colours.primary} />
          </View>
        }
        {mode === 'login' && (
          <>
            <View style={styles.login__input}>
              <Controller
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.login__input__text}
                    onBlur={onBlur}
                    autoCorrect={false}
                    onChangeText={onChange}
                    value={value}
                    placeholder="Email"
                  />
                )}
                name="email"
              />
              {errors.email && <Text style={GlobalStyle.required}>Email is required.</Text>}
            </View>

            <View style={styles.login__input}>
              <Controller
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.login__input__text}
                    onBlur={onBlur}
                    autoCorrect={false}
                    onChangeText={onChange}
                    value={value}
                    secureTextEntry
                    placeholder="Password"
                    returnKeyType="done"
                    onSubmitEditing={handleSubmit(onSubmit)}
                  />
                )}
                name="password"
              />
              {errors.password && <Text style={GlobalStyle.required}>Password is required.</Text>}
            </View>

            {loginResult?.error &&
              <Text style={GlobalStyle.required}>Invalid credentials. Please try again.</Text>
            }

            <TouchableOpacity
              style={styles.login__button}
              onPress={handleSubmit(onSubmit)}
            >
              <Text style={styles.login__button__text}>LOGIN</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => setMode('request')}>
              <Text style={{ marginTop: 10, textAlign: 'center', color: config.colours.primary }}>
                Forgot Password?
              </Text>
            </TouchableOpacity>
          </>
        )}

        {mode === 'request' && (
          <>
            <Text style={{ marginBottom: 10 }}>
              Enter your email to receive a reset code.
            </Text>

            <TextInput
              style={styles.login__input__text}
              value={resetEmail}
              onChangeText={setResetEmail}
              placeholder="Email"
              returnKeyType="done"
              onSubmitEditing={handleRequestReset}
            />

            {resetError && <Text style={GlobalStyle.required}>{resetError}</Text>}

            <TouchableOpacity
              style={styles.login__button}
              onPress={handleRequestReset}
            >
              <Text style={styles.login__button__text}>SEND CODE</Text>
            </TouchableOpacity>

            <TouchableOpacity onPress={() => setMode('login')}>
              <Text style={{ marginTop: 10, textAlign: 'center' }}>
                Back to Login
              </Text>
            </TouchableOpacity>
          </>
        )}

        {mode === 'reset' && (
          <>

          <Text>
              Enter the 4 digit code sent to your email.
            </Text>

            <TextInput
              style={[styles.login__input__text, { marginBottom: 10 }]}
              value={resetCode}
              onChangeText={setResetCode}
              keyboardType="numeric"
              maxLength={4}
              placeholder="4 Digit Code"
            />

            <Text>Enter new password</Text>
            <TextInput
              style={[styles.login__input__text, { marginBottom: 10 }]}
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              placeholder="New Password"
            />
            <Text>Confirm new password</Text>
            <TextInput
              style={[styles.login__input__text, { marginBottom: 10 }]}
              value={confirmNewPassword}
              onChangeText={setConfirmNewPassword}
              secureTextEntry
              placeholder="Confirm New Password"
              returnKeyType="done"
              onSubmitEditing={handleResetPassword}
            />

            {resetError && <Text style={GlobalStyle.required}>{resetError}</Text>}

            <TouchableOpacity
              style={styles.login__button}
              onPress={handleResetPassword}
            >
              <Text style={styles.login__button__text}>RESET PASSWORD</Text>
            </TouchableOpacity>
          </>
        )}

        {mode === 'forceReset' && (
          <>
              <Text>Enter current password</Text>
              <TextInput
                  style={[styles.login__input__text, { marginBottom: 10 }]}
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  secureTextEntry
                  placeholder="Current Password"
              />
              <Text>Enter new password</Text>
              <TextInput
                  style={[styles.login__input__text, { marginBottom: 10 }]}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  secureTextEntry
                  placeholder="New Password"
              />
              <Text>Confirm new password</Text>
              <TextInput
                  style={[styles.login__input__text, { marginBottom: 10 }]}
                  value={confirmNewPassword}
                  onChangeText={setConfirmNewPassword}
                  secureTextEntry
                  placeholder="Confirm New Password"
              />

              {resetError && <Text style={GlobalStyle.required}>{resetError}</Text>}

              <TouchableOpacity
                  style={styles.login__button}
                  onPress={handleForceResetPassword}
              >
                  <Text style={styles.login__button__text}>RESET PASSWORD</Text>
              </TouchableOpacity>
          </>
      )}

        <TouchableOpacity>
          <Text style={{ marginTop: 10, textAlign: 'center' }}>
            V: {VERSION}
          </Text>
        </TouchableOpacity>
      </View>
    </ImageBackground>
  );
}



const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },

    login: {
      backgroundColor: '#FFF',
      padding: 20,
      width: Platform.OS == 'web' ? 400 : '80%',
      borderRadius: 5,
      position: 'relative'
    },

      loader: {
        position: 'absolute',
        left: 0, top: 0, right: 0, bottom: 0,
        backgroundColor: '#FFF',
        zIndex: 999,
        alignItems: 'center',
        justifyContent: 'center'
      },

      login__input: {
        marginBottom: 10,
      },

        login__input__text: {
          borderWidth: 1,
          borderColor: '#CECECE',
          padding: 7,
          borderRadius: 5,
          fontFamily: 'OpenSans-Regular'
        },
      
      login__button: {
        backgroundColor: config.colours.primary,
        marginTop: 10,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: config.colours.primary,
      },

      login__button__text: {
        padding: 5,
        color: '#FFF',
        textAlign: 'center',
        fontFamily: 'OpenSans-ExtraBold'
      },

      sync__button: {
        backgroundColor: "#FFF",
        marginTop: 10,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: config.colours.primary,
      },

      sync__button__text: {
        padding: 5,
        color: config.colours.primary,
        textAlign: 'center',
        fontFamily: 'OpenSans-ExtraBold'
      }

});
